import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Sparkles, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Justice } from "@/components/Justice";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { Button } from "@/components/ui/button";
import { TIERS, CASE_PREP, CREDIT_PACK, trialCopy } from "@/lib/plans";

export default function PlanSelection({ onSkip }) {
  const [busy, setBusy] = useState("");
  const [founders, setFounders] = useState(null);

  useEffect(() => { api.get("/founders/remaining").then(({ data }) => setFounders(data)).catch((e) => console.error("load founders failed", e)); }, []);

  const startTrial = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const buyCasePrep = async () => {
    setBusy("case_prep");
    try {
      const { data } = await api.post("/billing/case-prep/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Select a case first, then unlock the Case Prep Pass");
      setBusy("");
    }
  };

  const chooseFree = () => {
    if (onSkip) onSkip();
    toast.success("You're on Basic Watch — free forever. Upgrade anytime.");
  };

  return (
    <div className="min-h-screen ek-scanline">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Justice size={92} />
          <div>
            <div className="ek-stamp inline-block mb-2">CHOOSE YOUR PROTECTION</div>
            <h1 className="font-head font-bold text-3xl sm:text-4xl tracking-tight leading-tight">Pick the plan that fits your case</h1>
            <p className="text-sm mt-2" style={{ color: "var(--ek-text-secondary)" }}>
              Start free, or begin a 7-day free trial. You are never charged without an explicit confirmation.
            </p>
          </div>
        </div>

        {founders && founders.remaining > 0 && (
          <div className="border rounded-lg px-4 py-3 mb-6 flex items-center gap-2 text-sm" style={{ background: "rgba(46,130,246,0.08)", borderColor: "rgba(46,130,246,0.3)" }} data-testid="founder-counter">
            <Sparkles className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
            <span><b style={{ color: "var(--ek-primary)" }}>{founders.remaining}</b> of {founders.total} free Founding Member spots remaining.</span>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          {TIERS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="border rounded-lg p-6 flex flex-col relative"
              style={{ background: "var(--ek-surface)", borderColor: p.featured ? "rgba(46,130,246,0.5)" : "var(--ek-border)" }}
              data-testid={`plan-${p.key}`}
            >
              {p.featured && <span className="ek-stamp absolute top-4 right-4" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.35)" }}>MOST PROTECTION</span>}
              <div className="ek-mono-label mb-2">{p.name.toUpperCase()}</div>
              <div className="flex items-end gap-1 mb-1">
                <span className="font-head font-bold text-4xl tracking-tight" data-testid={`plan-price-${p.key}`}>{p.priceLabel}</span>
                {p.price > 0 && <span className="text-sm mb-1" style={{ color: "var(--ek-text-muted)" }}>/month</span>}
              </div>
              <div className="ek-mono-label mb-4" style={{ color: p.trial ? "var(--ek-secure)" : "var(--ek-text-muted)" }}>
                {p.trial ? "7-DAY FREE TRIAL" : "FREE FOREVER"}
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--ek-text-secondary)" }}>
                    <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--ek-primary)" }} /> {f}
                  </li>
                ))}
              </ul>
              {p.trial ? (
                <>
                  <Button onClick={() => startTrial(p.key)} disabled={!!busy} data-testid={`start-trial-${p.key}`} className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                    {busy === p.key ? "REDIRECTING..." : "Start Your 7-Day Free Trial"}
                  </Button>
                  <p className="text-[11px] mt-2" style={{ color: "var(--ek-text-muted)" }} data-testid={`trial-terms-${p.key}`}>{trialCopy(p.price)}</p>
                </>
              ) : (
                <Button onClick={chooseFree} disabled={!!busy} data-testid={`choose-${p.key}`} variant="outline" className="w-full rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text)" }}>
                  Continue Free
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Case Prep Pass — one-time */}
        <div className="grid md:grid-cols-3 gap-5 mt-5">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
            className="border rounded-lg p-6 md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4"
            style={{ background: "var(--ek-surface)", borderColor: "rgba(46,130,246,0.25)" }} data-testid="plan-case_prep">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
                <span className="ek-mono-label">{CASE_PREP.name.toUpperCase()}</span>
                <span className="ek-stamp" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.35)" }}>ONE-TIME</span>
              </div>
              <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>{CASE_PREP.tagline}. For people who don't want an ongoing subscription — full Litigator Pro access for a single case.</p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-head font-bold text-3xl" data-testid="plan-price-case_prep">{CASE_PREP.priceLabel}</div>
              <Button onClick={buyCasePrep} disabled={!!busy} data-testid="buy-case-prep" className="mt-2 rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {busy === "case_prep" ? "REDIRECTING..." : "Get Case Prep Pass"}
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="border rounded-lg p-6 flex flex-col justify-center" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="credit-pack-note">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4" style={{ color: "var(--ek-primary)" }} /><span className="ek-mono-label">ADD KEEPERCREDITS</span></div>
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>
              Need more analyses? Buy <b style={{ color: "var(--ek-text)" }}>{CREDIT_PACK.credits} KeeperCredits for {CREDIT_PACK.priceLabel}</b> anytime. Subscription credits reset each billing anniversary; purchased credits never expire.
            </p>
          </motion.div>
        </div>

        <div className="mt-8 flex items-center gap-2 text-xs justify-center" style={{ color: "var(--ek-text-muted)" }}>
          <Lock className="h-3.5 w-3.5" /> No surprises. You confirm every purchase before any charge — required for App Store & Google Play.
        </div>

        {onSkip && (
          <div className="mt-6 text-center">
            <button onClick={onSkip} data-testid="skip-plan-btn" className="text-sm underline transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }}>
              Continue to the app for now — you can choose a plan anytime
            </button>
          </div>
        )}

        <DisclaimerFooter />
      </div>
    </div>
  );
}
